import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid,
} from "recharts";
import {
  ChevronLeft, ChevronRight, Sparkles, ArrowDownCircle, ArrowUpCircle, Scale,
  Wallet, AlertTriangle, Info, CheckCircle2, CalendarClock, Target, Clock, Landmark,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtEUR, fmtSigned, fmtEUR2, MONTHS_FR, fmtDateLong } from "@/lib/format";
import { CARD, Trend, HealthBadge, ProgressBar, Label, Loader, Stagger, Item } from "@/components/common";
import { SiteRevenueCard } from "@/components/SiteRevenue";
import { InvoiceDueAlert } from "@/components/invoices/InvoiceDueAlert";

const now = new Date();

function toneOfTreasury(v, threshold) {
  if (v < 0) return { c: "text-rose-600", bg: "bg-rose-50", label: "Risque de trésorerie" };
  if (v < threshold) return { c: "text-amber-600", bg: "bg-amber-50", label: "Vigilance" };
  return { c: "text-emerald-600", bg: "bg-emerald-50", label: "Situation confortable" };
}

function KpiCard({ icon: Icon, label, value, tone = "default", sub }) {
  const tones = {
    default: "text-gray-900", green: "text-emerald-600", red: "text-rose-600", amber: "text-amber-600",
  };
  return (
    <Item className={`${CARD} p-5`}>
      <div className="flex items-center gap-2 text-gray-400">
        <Icon className="h-4 w-4" />
        <Label>{label}</Label>
      </div>
      <p className={`mt-2 font-display text-2xl font-black sm:text-3xl ${tones[tone]}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-gray-400">{sub}</p>}
    </Item>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-lg">
      <p className="font-display text-sm font-bold">{d.label}</p>
      <p className="text-xs text-emerald-600">Entrées : {fmtEUR(d.entrees)}</p>
      <p className="text-xs text-rose-600">Dépenses : {fmtEUR(d.depenses)}</p>
      <p className="text-xs text-gray-500">Résultat : {fmtSigned(d.resultat)}</p>
      <p className="mt-1 text-xs font-bold">Trésorerie : {fmtEUR(d.tresorerie)}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [data, setData] = useState(null);
  const [assistant, setAssistant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get(`/dashboard?year=${ym.year}&month=${ym.month}`);
      setData(data);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
    api.get(`/assistant?year=${ym.year}&month=${ym.month}`).then((r) => setAssistant(r.data)).catch(() => {});
  }, [ym]);

  useEffect(() => { load(); }, [load]);

  const changeMonth = (delta) => {
    let m = ym.month + delta, y = ym.year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setYm({ year: y, month: m });
  };

  if (loading) return <Loader />;
  if (error || !data)
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="dashboard-error">
        <div className="rounded-2xl bg-rose-50 p-4">
          <AlertTriangle className="h-8 w-8 text-rose-500" />
        </div>
        <p className="mt-4 font-display text-lg font-bold">Impossible de charger votre tableau de bord</p>
        <p className="mt-1 max-w-sm text-sm text-gray-500">
          Vérifiez votre connexion puis réessayez. Si le problème persiste, reconnectez-vous.
        </p>
        <button
          data-testid="dashboard-retry"
          onClick={load}
          className="mt-5 rounded-full bg-[#065F46] px-6 py-2.5 font-semibold text-white hover:bg-[#047857]"
        >
          Réessayer
        </button>
      </div>
    );

  const c = data.current, nx = data.next;
  const threshold = data.settings?.safety_threshold || 0;
  const tone = toneOfTreasury(c.tresorerie_fin, threshold);
  const firstName = (user?.name || "").split(" ")[0] || "";

  return (
    <div>
      {/* Greeting + month nav */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-400">Bonjour {firstName} 👋</p>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Situation financière</h1>
        </div>
        <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-white p-1">
          <button data-testid="prev-month" onClick={() => changeMonth(-1)} className="rounded-full p-1.5 hover:bg-gray-100"><ChevronLeft className="h-4 w-4" /></button>
          <span data-testid="current-month-label" className="min-w-[92px] text-center text-sm font-semibold">{MONTHS_FR[ym.month]} {ym.year}</span>
          <button data-testid="next-month" onClick={() => changeMonth(1)} className="rounded-full p-1.5 hover:bg-gray-100"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      {(user?.is_superadmin || ["owner", "admin"].includes(user?.role)) && <InvoiceDueAlert />}

      <Stagger className="mt-6 space-y-5">
        {/* Hero card */}
        <Item className={`${CARD} relative overflow-hidden p-6 sm:p-8`}>
          <div className={`absolute -right-16 -top-16 h-56 w-56 rounded-full ${tone.bg} opacity-60 blur-2xl`} />
          <div className="relative">
            <div className="flex items-center justify-between">
              <Label>Trésorerie estimée fin de mois</Label>
              <HealthBadge level={data.health.level} />
            </div>
            <p data-testid="hero-balance-value" className={`mt-3 font-display text-5xl font-black tracking-tighter sm:text-6xl ${tone.c}`}>
              {fmtEUR(c.tresorerie_fin)}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Trend value={c.tendance} suffix="ce mois-ci" />
              <span className={`text-sm font-semibold ${tone.c}`}>{tone.label}</span>
            </div>
            <p className="mt-2 text-sm text-gray-400">Si les prévisions actuelles se réalisent.</p>
          </div>
        </Item>

        {/* 4 KPIs */}
        <div className="grid grid-cols-2 gap-4">
          <KpiCard icon={ArrowDownCircle} label="Entrées prévues" value={fmtEUR(c.entrees_prevues)} tone="green" />
          <KpiCard icon={ArrowUpCircle} label="Dépenses prévues" value={fmtEUR(c.depenses_prevues)} tone="red" />
          <KpiCard icon={Scale} label="Résultat du mois" value={fmtSigned(c.resultat)} tone={c.resultat >= 0 ? "green" : "red"} />
          <KpiCard icon={Wallet} label="Trésorerie fin de mois" value={fmtEUR(c.tresorerie_fin)} tone={c.tresorerie_fin < 0 ? "red" : c.tresorerie_fin < threshold ? "amber" : "green"} />
        </div>

        {/* Position de trésorerie : en banque vs en attente */}
        {data.pending_transfers && (
          <Item className={`${CARD} p-6`} data-testid="cash-position-card">
            <div className="flex items-center gap-2 text-gray-400"><Landmark className="h-4 w-4" /><Label>Position de trésorerie</Label></div>
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-2xl bg-emerald-50 p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Déjà en banque</p>
                <p data-testid="cash-in-bank" className="mt-1 font-display text-2xl font-black text-emerald-700">{fmtEUR(data.cash_in_bank)}</p>
                <p className="mt-1 text-xs text-emerald-600/70">Encaissements réalisés − dépenses payées</p>
              </div>
              <div className="rounded-2xl bg-sky-50 p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">En attente de virement</p>
                <p data-testid="pending-transfers-total" className="mt-1 font-display text-2xl font-black text-sky-700">{fmtEUR(data.pending_transfers.total)}</p>
                <p className="mt-1 text-xs text-sky-600/80">Sécu/CMU {fmtEUR(data.pending_transfers.secu)} · Mutuelles {fmtEUR(data.pending_transfers.mutuelle)}{data.pending_transfers.alma ? ` · Alma ${fmtEUR(data.pending_transfers.alma)}` : ""}</p>
              </div>
            </div>
            {data.pending_transfers.count > 0 && (
              <p className="mt-3 text-xs text-gray-500">{data.pending_transfers.count} virement(s) à venir{data.pending_transfers.next_date ? `, prochain le ${fmtDateLong(data.pending_transfers.next_date)}` : ""}. À pointer dans Encaissements dès réception.</p>
            )}
          </Item>
        )}

        {/* Assistant */}
        <Item className={`${CARD} bg-emerald-50/60 p-6`}>
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
              <Sparkles className="h-5 w-5" />
            </span>
            <div>
              <Label className="text-emerald-700">Votre copilote financier</Label>
              <p data-testid="assistant-narrative" className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-gray-700">
                {(assistant?.narrative && assistant.narrative.replace(/\*\*/g, "").replace(/^#+\s*/gm, "").trim())
                  || (assistant?.sentences || []).join(" ")
                  || `Ce mois-ci, ${c.resultat >= 0 ? `vos entrées dépassent vos dépenses de ${fmtEUR(c.resultat)}` : `vos dépenses dépassent vos recettes de ${fmtEUR(Math.abs(c.resultat))}`}. Si les prévisions se réalisent, vous devriez terminer ${c.label.toLowerCase()} avec environ ${fmtEUR(c.tresorerie_fin)} de trésorerie.`}
              </p>
            </div>
          </div>
        </Item>

        {/* Progress */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Item className={`${CARD} p-6`}>
            <div className="flex items-center justify-between">
              <Label>Encaissements</Label>
              <span className="font-display text-lg font-bold text-emerald-600">{c.progression_encaissements}%</span>
            </div>
            <div className="mt-3"><ProgressBar value={c.progression_encaissements} color="emerald" height="h-4" /></div>
            <div className="mt-3 flex justify-between text-xs text-gray-500">
              <span>Déjà encaissé : <b className="text-gray-800">{fmtEUR(c.deja_encaisse)}</b></span>
              <span>Reste : <b className="text-gray-800">{fmtEUR(c.reste_a_encaisser)}</b></span>
            </div>
          </Item>
          <Item className={`${CARD} p-6`}>
            <div className="flex items-center justify-between">
              <Label>Dépenses payées</Label>
              <span className="font-display text-lg font-bold text-amber-600">{c.progression_depenses}%</span>
            </div>
            <div className="mt-3"><ProgressBar value={c.progression_depenses} color="amber" height="h-4" /></div>
            <div className="mt-3 flex justify-between text-xs text-gray-500">
              <span>Déjà payé : <b className="text-gray-800">{fmtEUR(c.deja_paye)}</b></span>
              <span>Reste : <b className="text-gray-800">{fmtEUR(c.reste_a_payer)}</b></span>
            </div>
          </Item>
        </div>

        {/* CA par site */}
        <Item><SiteRevenueCard year={ym.year} month={ym.month} /></Item>

        {/* 12 month chart */}
        <Item className={`${CARD} p-6`}>
          <div className="flex items-center justify-between">
            <div>
              <Label>Trésorerie prévisionnelle</Label>
              <p className="font-display text-lg font-bold">Sur 12 mois</p>
            </div>
          </div>
          <div className="mt-4 h-64" data-testid="treasury-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.twelve_months} margin={{ top: 10, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#065F46" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#065F46" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F2F4" />
                <XAxis dataKey="short" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip content={<ChartTooltip />} />
                {threshold > 0 && <ReferenceLine y={threshold} stroke="#F59E0B" strokeDasharray="4 4" />}
                <ReferenceLine y={0} stroke="#EF4444" strokeDasharray="4 4" />
                <Area type="monotone" dataKey="tresorerie" stroke="#065F46" strokeWidth={2.5} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Item>

        {/* Next month */}
        <Item className={`${CARD} p-6`}>
          <Label>Le mois prochain</Label>
          <p className="font-display text-xl font-bold">{nx.label}</p>
          <div className="mt-4 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-2xl bg-emerald-50 p-3">
              <p className="text-[11px] font-semibold text-emerald-700">Entrées</p>
              <p className="font-display text-base font-bold text-emerald-700">{fmtEUR(nx.entrees_prevues)}</p>
            </div>
            <div className="rounded-2xl bg-rose-50 p-3">
              <p className="text-[11px] font-semibold text-rose-700">Dépenses</p>
              <p className="font-display text-base font-bold text-rose-700">{fmtEUR(nx.depenses_prevues)}</p>
            </div>
            <div className={`rounded-2xl p-3 ${nx.resultat >= 0 ? "bg-emerald-50" : "bg-amber-50"}`}>
              <p className={`text-[11px] font-semibold ${nx.resultat >= 0 ? "text-emerald-700" : "text-amber-700"}`}>Résultat</p>
              <p className={`font-display text-base font-bold ${nx.resultat >= 0 ? "text-emerald-700" : "text-amber-700"}`}>{fmtSigned(nx.resultat)}</p>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-600">
            Avec les prévisions actuelles, le cabinet devrait terminer {nx.label.toLowerCase()} avec environ{" "}
            <b>{fmtEUR(nx.tresorerie_fin)}</b> de trésorerie.
          </p>
        </Item>

        {/* Breakeven + 30 days */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Item className={`${CARD} p-6`}>
            <div className="flex items-center gap-2 text-gray-400"><Target className="h-4 w-4" /><Label>Votre objectif du mois</Label></div>
            <p className="mt-2 text-sm text-gray-600">Le cabinet doit générer environ</p>
            <p className="font-display text-2xl font-black">{fmtEUR(data.breakeven.objectif_ca)}</p>
            <p className="text-xs text-gray-400">pour couvrir toutes ses dépenses.</p>
            <div className="mt-4"><ProgressBar value={data.breakeven.progression} color={data.breakeven.atteint ? "emerald" : "amber"} /></div>
            <p className="mt-2 text-sm">
              {data.breakeven.atteint
                ? <span className="font-semibold text-emerald-600">Objectif atteint ✓</span>
                : <span className="font-semibold text-amber-600">Manque {fmtEUR(data.breakeven.manque)} pour l'équilibre</span>}
            </p>
          </Item>
          <Item className={`${CARD} p-6`}>
            <div className="flex items-center gap-2 text-gray-400"><Clock className="h-4 w-4" /><Label>Les 30 prochains jours</Label></div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Aujourd'hui</span><b>{fmtEUR(data.thirty_days.aujourdhui)}</b></div>
              <div className="flex justify-between"><span className="text-gray-500">Entrées attendues</span><b className="text-emerald-600">+{fmtEUR(data.thirty_days.entrees_attendues)}</b></div>
              <div className="flex justify-between"><span className="text-gray-500">Dépenses attendues</span><b className="text-rose-600">−{fmtEUR(data.thirty_days.depenses_attendues)}</b></div>
              <div className="flex justify-between border-t border-gray-100 pt-2"><span className="font-semibold">Dans 30 jours</span><b className="font-display text-lg">{fmtEUR(data.thirty_days.dans_30_jours)}</b></div>
            </div>
          </Item>
        </div>

        {/* Alerts */}
        {data.alerts?.length > 0 && (
          <Item className={`${CARD} p-6`}>
            <Label className="mb-3">À surveiller</Label>
            <div className="space-y-2">
              {data.alerts.map((a, i) => {
                const map = { danger: ["bg-rose-50", "text-rose-700", AlertTriangle], warning: ["bg-amber-50", "text-amber-800", AlertTriangle], info: ["bg-blue-50", "text-blue-700", Info] };
                const [bg, tc, Icon] = map[a.level] || map.info;
                return (
                  <div key={i} data-testid={`alert-${i}`} className={`flex items-start gap-3 rounded-2xl ${bg} p-3`}>
                    <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tc}`} />
                    <div><p className={`text-sm font-semibold ${tc}`}>{a.title}</p><p className="text-xs text-gray-600">{a.text}</p></div>
                  </div>
                );
              })}
            </div>
          </Item>
        )}

        {/* Upcoming + Top expenses */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Item className={`${CARD} p-6`}>
            <div className="mb-3 flex items-center gap-2 text-gray-400"><CalendarClock className="h-4 w-4" /><Label>Prochaines échéances</Label></div>
            <div className="space-y-1">
              {data.upcoming.length === 0 && <p className="text-sm text-gray-400">Aucune échéance à venir.</p>}
              {data.upcoming.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-gray-400">{fmtDateLong(t.transaction_date)}</span>
                    <span className="text-sm font-medium">{t.label}</span>
                  </div>
                  <span className="font-display text-sm font-bold text-rose-600">−{fmtEUR(t.amount)}</span>
                </div>
              ))}
            </div>
          </Item>
          <Item className={`${CARD} p-6`}>
            <Label className="mb-3">Top 5 dépenses</Label>
            <div className="space-y-2.5">
              {data.top_expenses.map((e, i) => {
                const max = data.top_expenses[0]?.amount || 1;
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm"><span className="font-medium">{e.name}</span><span className="font-semibold">{fmtEUR(e.amount)}</span></div>
                    <div className="mt-1"><ProgressBar value={(e.amount / max) * 100} color="rose" height="h-2" /></div>
                  </div>
                );
              })}
            </div>
          </Item>
        </div>

        {/* Health reasons */}
        <Item className={`${CARD} p-6`}>
          <div className="mb-3 flex items-center justify-between"><Label>Santé financière</Label><HealthBadge level={data.health.level} /></div>
          <div className="space-y-1.5">
            {data.health.reasons.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                {r.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}
                <span className="text-gray-600">{r.text}</span>
              </div>
            ))}
          </div>
        </Item>
      </Stagger>
    </div>
  );
}
